import { useTagPlan } from 'features/tagPlan/business/useTagPlan';
import Head from 'next/head';
import React, { ComponentProps, FC, PropsWithChildren, useEffect, useMemo } from 'react';
import { TextProps } from '@components/Text/Text.types';
import { useTranslation } from '@locales/useTranslation.hook';
import classnames from 'classnames';
import { PlcTagProps } from 'features/tagPlan/business/TagPlan.types';

export type MetasProps = {
  metaTitleI18n: string;
  metaTitleOptions?: TextProps['i18nOptions'];
  metaDescI18n?: string;
  metaDescOptions?: TextProps['i18nOptions'];
  appNameAsPrefix?: boolean;
  canonical?: string;
};

type Props = ComponentProps<'main'> & MetasProps & { tagProps?: PlcTagProps };

const PlcMetaHead: FC<MetasProps> = ({
  metaTitleI18n,
  metaTitleOptions,
  metaDescI18n,
  metaDescOptions,
  appNameAsPrefix,
  canonical,
}) => {
  const { t } = useTranslation(['common']);
  const appName = t('common.app-name');

  const fullTitle = useMemo(() => {
    const title = t(metaTitleI18n, metaTitleOptions);
    return appNameAsPrefix ? `${appName} - ${title}` : `${title} - ${appName}`;
  }, [metaTitleI18n, metaTitleOptions, appNameAsPrefix]);

  useEffect(() => {
    document.title = fullTitle;
  }, []);

  return (
    <>
      <Head>
        <title key="title">{fullTitle}</title>
        {metaDescI18n && (
          <meta content={`${t(metaDescI18n, metaDescOptions)} - ${appName}`} key="description" name="description" />
        )}
        {canonical && <link href={canonical} key="canonical" rel="canonical" />}
      </Head>
    </>
  );
};

const PlcPageLayout: FC<PropsWithChildren<Props>> = ({
  className,
  children,
  metaTitleI18n,
  metaTitleOptions,
  metaDescI18n,
  metaDescOptions,
  appNameAsPrefix,
  canonical,
  tagProps,
  ...rest
}) => {
  const rootClass = classnames('plc-page', className);

  const { tagPage } = useTagPlan();
  useEffect(() => {
    if (!tagProps || (tagProps && tagProps.isDataLoading)) {
      return;
    }
    tagPage(tagProps);
  }, [tagProps]);

  return (
    <main className={rootClass} {...rest}>
      <PlcMetaHead
        appNameAsPrefix={appNameAsPrefix}
        canonical={canonical}
        metaDescI18n={metaDescI18n}
        metaDescOptions={metaDescOptions}
        metaTitleI18n={metaTitleI18n}
        metaTitleOptions={metaTitleOptions}
      />
      {children}
    </main>
  );
};

export { PlcPageLayout, PlcMetaHead };
